import { Box } from '@mui/material';
import { Icon } from '@ui/Icon';
import React, { useCallback, useMemo } from 'react';

import { useOptimisticBoolean } from './hooks/use-optimistic-boolean.hook';
import { BaseMenuItem } from './BaseMenuItem';

export type MenuCheckboxItemProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  checkboxColor?: string;
  uncheckedIcon?: React.ReactNode;
  disabled?: boolean;
  children: React.ReactNode;
  searchText?: string;
};

export const MenuCheckboxItem: React.FC<MenuCheckboxItemProps> = ({
  checked,
  onChange,
  checkboxColor,
  uncheckedIcon,
  disabled = false,
  children,
  searchText,
}) => {
  const [value, toggle] = useOptimisticBoolean(checked, onChange);

  const icon = useMemo(
    () => (
      <Box
        sx={{
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: checkboxColor ?? 'inherit',
        }}
      >
        {value ? <Icon category="mui" icon="Check" /> : uncheckedIcon}
      </Box>
    ),
    [value, checkboxColor, uncheckedIcon],
  );

  const handleClick = useCallback(() => {
    if (!disabled) toggle(!value);
  }, [disabled, toggle, value]);

  return (
    <BaseMenuItem
      icon={icon}
      searchText={searchText}
      onClick={handleClick}
      disabled={disabled}
      closeOnClick={false}
      selected={value}
      aria-pressed={value}
    >
      {children}
    </BaseMenuItem>
  );
};

MenuCheckboxItem.displayName = 'Menu.CheckboxItem';
